let isSelecting = false;
let firstSpanUnderMouse = null;
let lastSpanUnderMouse = null;
let isTouchDevice = false;
let touchStartTime = 0;
let lastTouchTarget = null;
let activeLookupId = null;

export const startSelection = (
    handleWordLookUp,
    tooltipRef,
    isTooltipShowing = null,
    setForceHideTooltip = null,
    setShowTooltip = null,
    setShowWordLookupLoader = null,
    setTooltipContent = null,
    containerRef = null,
    setIsHighlighting = null
) => {
    const isTranslatable = element => {
        if (!element) return false;
        return !!element.closest('[data-translatable="true"]');
    };

    const isWithinContainer = element => {
        if (!containerRef || !containerRef.current) return true;
        return containerRef.current.contains(element);
    };

    const isSpanSelectable = span => {
        if (!span) return false;
        return isWithinContainer(span);
    };

    const updateHighlights = (startNode, endNode) => {
        if (!startNode || !endNode) return;

        const container = startNode.parentNode;
        const spans = Array.from(container.querySelectorAll('span'));
        const startIndex = spans.indexOf(startNode);
        const endIndex = spans.indexOf(endNode);

        spans.forEach(span => {
            span.classList.remove('highlighted');
        });

        const [fromIndex, toIndex] =
            startIndex <= endIndex
                ? [startIndex, endIndex]
                : [endIndex, startIndex];
        spans.slice(fromIndex, toIndex + 1).forEach(span => {
            span.classList.add('highlighted');
        });
    };

    const handleMouseMove = event => {
        if (!isSelecting || isTouchDevice) return;
        if (!isWithinContainer(event.target)) return;

        let spanUnderMouse = event.target.closest('span');
        if (spanUnderMouse && !isTranslatable(spanUnderMouse)) {
            spanUnderMouse = null;
        }
        if (spanUnderMouse && !isSpanSelectable(spanUnderMouse)) {
            spanUnderMouse = null;
        }

        if (!spanUnderMouse && firstSpanUnderMouse) {
            const container = firstSpanUnderMouse.parentNode;
            const spans = Array.from(container.querySelectorAll('span'));
            const mouseY = event.clientY;
            let closestSpan = null;
            let closestDistance = Infinity;

            spans.forEach(span => {
                const rect = span.getBoundingClientRect();
                if (mouseY >= rect.top && mouseY <= rect.bottom) {
                    const distance =
                        event.clientX >= rect.left
                            ? event.clientX <= rect.right
                                ? 0
                                : event.clientX - rect.right
                            : rect.left - event.clientX;

                    if (distance < closestDistance) {
                        closestDistance = distance;
                        closestSpan = span;
                    }
                }
            });

            spanUnderMouse = closestSpan;
        }

        if (spanUnderMouse) {
            lastSpanUnderMouse = spanUnderMouse;
            updateHighlights(firstSpanUnderMouse, lastSpanUnderMouse);
        }
    };

    const handleTouchMove = event => {
        if (!isSelecting || !isTouchDevice) return;

        const touch = event.touches[0];
        if (!touch) return;

        const element = document.elementFromPoint(touch.clientX, touch.clientY);
        if (element && !isWithinContainer(element)) return;

        let spanUnderTouch = element ? element.closest('span') : null;
        if (spanUnderTouch && !isTranslatable(spanUnderTouch)) {
            spanUnderTouch = null;
        }
        if (spanUnderTouch && !isSpanSelectable(spanUnderTouch)) {
            spanUnderTouch = null;
        }

        if (!spanUnderTouch && firstSpanUnderMouse) {
            const container = firstSpanUnderMouse.parentNode;
            const spans = Array.from(container.querySelectorAll('span'));
            const touchY = touch.clientY;
            let closestSpan = null;
            let closestDistance = Infinity;

            spans.forEach(span => {
                const rect = span.getBoundingClientRect();
                if (touchY >= rect.top && touchY <= rect.bottom) {
                    const distance =
                        touch.clientX >= rect.left
                            ? touch.clientX <= rect.right
                                ? 0
                                : touch.clientX - rect.right
                            : rect.left - touch.clientX;

                    if (distance < closestDistance) {
                        closestDistance = distance;
                        closestSpan = span;
                    }
                }
            });

            spanUnderTouch = closestSpan;
        }

        if (spanUnderTouch && spanUnderTouch.tagName === 'SPAN') {
            event.preventDefault();

            if (spanUnderTouch !== lastTouchTarget) {
                lastTouchTarget = spanUnderTouch;
                lastSpanUnderMouse = spanUnderTouch;
                updateHighlights(firstSpanUnderMouse, lastSpanUnderMouse);
            }
        }
    };

    const handleMouseDown = event => {
        if (!isWithinContainer(event.target)) return;

        const spanUnderMouse = event.target.closest('span');
        if (spanUnderMouse && !isTranslatable(spanUnderMouse)) return;
        if (spanUnderMouse && !isSpanSelectable(spanUnderMouse)) return;

        // Close any open tooltip when starting a new selection
        if (setShowTooltip) {
            setShowTooltip(false);
        }

        isTouchDevice = false;
        isSelecting = true;

        if (setIsHighlighting && spanUnderMouse) {
            setIsHighlighting(true);
        }

        if (spanUnderMouse) {
            firstSpanUnderMouse = spanUnderMouse;
            lastSpanUnderMouse = spanUnderMouse;
        }

        const selection = window.getSelection();
        if (selection) {
            selection.removeAllRanges();
        }

        const container = containerRef?.current || document;
        const spans = container.querySelectorAll('span.highlighted');
        spans.forEach(span => {
            span.classList.remove('highlighted');
        });

        const clickedSpan = event.target.closest('span');
        if (clickedSpan && isTranslatable(clickedSpan)) {
            clickedSpan.classList.add('highlighted');
        }
    };

    const handleTouchStart = event => {
        const touch = event.touches[0];
        if (!touch) return;

        const element = document.elementFromPoint(touch.clientX, touch.clientY);
        if (element && !isWithinContainer(element)) return;

        const spanUnderTouch = element ? element.closest('span') : null;
        if (!spanUnderTouch || !isTranslatable(spanUnderTouch)) {
            return;
        }
        if (!isSpanSelectable(spanUnderTouch)) return;

        // Close any open tooltip when starting a new selection
        if (setShowTooltip) {
            setShowTooltip(false);
        }

        isTouchDevice = true;
        isSelecting = true;
        touchStartTime = Date.now();

        if (setIsHighlighting) {
            setIsHighlighting(true);
        }

        firstSpanUnderMouse = spanUnderTouch;
        lastSpanUnderMouse = spanUnderTouch;
        lastTouchTarget = spanUnderTouch;

        const selection = window.getSelection();
        if (selection) {
            selection.removeAllRanges();
        }

        const container = containerRef?.current || document;
        const spans = container.querySelectorAll('span.highlighted');
        spans.forEach(span => {
            span.classList.remove('highlighted');
        });

        if (spanUnderTouch) {
            spanUnderTouch.classList.add('highlighted');
        }
    };

    const processSelection = (startNode, endNode) => {
        if (!startNode || !endNode) return;

        const container = startNode.parentNode;
        const spans = Array.from(container.querySelectorAll('span'));
        const startIndex = spans.indexOf(startNode);
        const endIndex = spans.indexOf(endNode);

        const [firstIndex, lastIndex] =
            startIndex < endIndex
                ? [startIndex, endIndex]
                : [endIndex, startIndex];

        const fullSelection = spans
            .slice(firstIndex, lastIndex + 1)
            .map(span => span.textContent.trim())
            .join(' ');

        const selectedSpans = spans.slice(firstIndex, lastIndex + 1);
        let minLeft = Infinity;
        let maxRight = -Infinity;

        selectedSpans.forEach(span => {
            const rect = span.getBoundingClientRect();
            minLeft = Math.min(minLeft, rect.left);
            maxRight = Math.max(maxRight, rect.right);
        });

        const midXPos = (minLeft + maxRight) / 2;
        const topPos = startNode.getBoundingClientRect().top;

        const tooltipCurrentlyShowing = isTooltipShowing && isTooltipShowing();
        const delayMs = tooltipCurrentlyShowing ? 50 : 0;

        if (tooltipCurrentlyShowing) {
            if (setTooltipContent) {
                setTooltipContent('');
            }
            if (setShowTooltip) {
                setShowTooltip(false);
            }
            if (setForceHideTooltip) {
                setForceHideTooltip(true);
                setTimeout(() => setForceHideTooltip(false), 50);
            }
        }

        setTimeout(() => {
            const lookupId = Date.now() + Math.random();
            activeLookupId = lookupId;

            const containerElement =
                selectedSpans.length > 1 ? startNode.parentNode : selectedSpans[0];

            handleWordLookUp(fullSelection, topPos, midXPos, containerElement, lookupId);

            firstSpanUnderMouse = null;
            lastSpanUnderMouse = null;
            lastTouchTarget = null;
            isSelecting = false;
            isTouchDevice = false;
            window.getSelection().removeAllRanges();
        }, delayMs);
    };

    const handleMouseUp = event => {
        if (isTouchDevice) return;

        isSelecting = false;

        const startNode = firstSpanUnderMouse;
        const endNode = lastSpanUnderMouse;

        if (!startNode || !endNode) return;

        if (startNode === endNode) {
            const container = containerRef?.current || document;
            const spans = container.querySelectorAll('span.highlighted');
            spans.forEach(span => {
                if (span !== startNode) {
                    span.classList.remove('highlighted');
                }
            });
            startNode.classList.add('highlighted');
        }

        event.preventDefault();
        event.stopPropagation();

        processSelection(startNode, endNode);
    };

    const handleTouchEnd = event => {
        if (!isTouchDevice) return;

        isSelecting = false;

        const startNode = firstSpanUnderMouse;
        const endNode = lastSpanUnderMouse;

        if (!startNode || !endNode) return;

        const touchDuration = Date.now() - touchStartTime;
        const isQuickTap = touchDuration < 200;

        if (isQuickTap && startNode === endNode) {
            const container = containerRef?.current || document;
            const spans = container.querySelectorAll('span.highlighted');
            spans.forEach(span => {
                if (span !== startNode) {
                    span.classList.remove('highlighted');
                }
            });
            startNode.classList.add('highlighted');
        } else {
            event.preventDefault();
            event.stopPropagation();
        }

        processSelection(startNode, endNode);
    };

    const handleClickOutside = event => {
        isSelecting = false;

        const selection = window.getSelection();
        if (selection && !selection.isCollapsed) {
            return;
        }

        const tooltip = tooltipRef?.current;
        const clickedSpan = event.target.closest('span');
        const isClickOnTranslatableSpan = clickedSpan && isTranslatable(clickedSpan);

        const isClickInContainer = isWithinContainer(event.target);
        const isClickOnTooltip = tooltip && tooltip.contains(event.target);

        const isClickOutside =
            !isClickOnTranslatableSpan && (!tooltip || !isClickOnTooltip);

        if (isClickOutside && (isClickInContainer || firstSpanUnderMouse)) {
            const container = containerRef?.current || document;
            const highlightedSpans = container.querySelectorAll('span.highlighted');
            highlightedSpans.forEach(span => {
                span.classList.remove('highlighted');
            });

            activeLookupId = null;

            if (setIsHighlighting) {
                setIsHighlighting(false);
            }

            if (setShowTooltip) {
                setShowTooltip(false);
            }

            if (setShowWordLookupLoader) {
                setShowWordLookupLoader(false);
            }

            firstSpanUnderMouse = null;
            lastSpanUnderMouse = null;
            lastTouchTarget = null;
            isTouchDevice = false;

            window.getSelection().removeAllRanges();
        }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchstart', handleTouchStart, true);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mousedown', handleMouseDown, true);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchstart', handleTouchStart, true);
        document.removeEventListener('touchend', handleTouchEnd);
    };
};

export const isLookupActive = lookupId => {
    return activeLookupId === lookupId;
};
